// Stage 33 / 29 follow-up: panel chrome with footer toolbar.
//
// Removed in this iteration: the cog button + cog popover menu. The
// cog menu had no good place to put the bind selection list on small
// panels and obscured part of the panel chrome itself. Replaced by a
// persistent footer toolbar carrying the same actions plus a popover
// anchored above the Bind button for task selection.
//
// Header keeps:
//   - drag handle (whole header bar)
//   - koji slug (click to copy)
//   - display label (double-click to rename)
//   - status pill
//
// Footer carries:
//   - Bind / Re-bind (opens popover)
//   - Unbind (only when bound)
//   - History (only when at least one run has output)
//   - Delete (always; placed at the right edge, before the resize handle)
//
// The History drawer remains a slide-in side drawer because its
// content (R2 history list + iframe preview) is wide enough to
// warrant its own surface.

import { useCallback, useEffect, useRef, useState } from 'react';
import { BulletinPanelBody } from './BulletinPanelBody';
import { BindTaskPopover } from './BindTaskPopover';
import { HistoryDrawer } from './HistoryDrawer';
import {
  bindPanel,
  deletePanel as apiDeletePanel,
  getPanelHistory,
  pinVersion,
  renamePanel,
  setCollapsed as apiSetCollapsed,
} from './bulletinClient';
import type { BulletinPanelDTO, PanelHistoryEntry } from './types';

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatVersionLabel(entry: PanelHistoryEntry): string {
  const ts = new Date(entry.uploaded);
  const ext = entry.key.endsWith('.html') ? 'html' : 'md';
  const localised =
    `${ts.getFullYear()}-${pad2(ts.getMonth() + 1)}-${pad2(ts.getDate())} ` +
    `${pad2(ts.getHours())}:${pad2(ts.getMinutes())}`;
  return `${localised} (${ext})`;
}

export function BulletinPanel({
  panel,
  scale,
  onMove,
  onResize,
  onDelete,
  onUpdate,
  onRefresh,
}: {
  panel: BulletinPanelDTO;
  scale: number;
  onMove: (id: string, x: number, y: number) => void;
  onResize: (id: string, w: number, h: number) => void;
  onDelete: (id: string) => void;
  onUpdate: (next: BulletinPanelDTO) => void;
  onRefresh: () => Promise<void>;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [bindOpen, setBindOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Stage 39: optimistic local mirror of panel.collapsed so the toggle
  // is immediate. Reconciled with the prop on each refresh.
  const [collapsed, setCollapsed] = useState<boolean>(panel.collapsed);
  useEffect(() => {
    setCollapsed(panel.collapsed);
  }, [panel.collapsed]);
  // Stage 33: lazy-loaded history list for the version dropdown.
  // Null = not yet fetched; [] = fetched and empty; otherwise = list.
  const [versions, setVersions] = useState<PanelHistoryEntry[] | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);
  const editRef = useRef<HTMLInputElement>(null);

  // Stage 33: load history on first dropdown open. Reuses the same
  // /history endpoint the HistoryDrawer uses.
  const loadHistory = useCallback(async (): Promise<void> => {
    if (versions !== null) return;
    try {
      const fetched = await getPanelHistory(panel.id);
      setVersions(fetched);
    } catch (err) {
      console.warn('[bulletin] history fetch failed:', err);
      setVersions([]);
    }
  }, [panel.id, versions]);

  // Stage 33: invalidate the local cache when last_run_at bumps —
  // implies a new historical version may have appeared.
  useEffect(() => {
    setVersions(null);
  }, [panel.lastRunAt]);

  const handlePinChange = async (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): Promise<void> => {
    const value = e.target.value;
    const next = value === '' ? null : value;
    setPinError(null);
    try {
      await pinVersion(panel.id, next);
      onUpdate({ ...panel, pinnedR2Key: next });
    } catch (err) {
      setPinError(err instanceof Error ? err.message : String(err));
    }
  };

  const onHeaderMouseDown = (e: React.MouseEvent): void => {
    if ((e.target as HTMLElement).closest('button, input, select')) return;
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPanelX = panel.x;
    const startPanelY = panel.y;
    const onMouseMove = (m: MouseEvent): void => {
      const dx = (m.clientX - startX) / scale;
      const dy = (m.clientY - startY) / scale;
      onMove(panel.id, Math.round(startPanelX + dx), Math.round(startPanelY + dy));
    };
    const onMouseUp = (): void => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const onResizeMouseDown = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = panel.w;
    const startH = panel.h;
    const onMouseMove = (m: MouseEvent): void => {
      const dw = (m.clientX - startX) / scale;
      const dh = (m.clientY - startY) / scale;
      onResize(
        panel.id,
        Math.max(240, Math.min(2000, Math.round(startW + dw))),
        Math.max(180, Math.min(2000, Math.round(startH + dh))),
      );
    };
    const onMouseUp = (): void => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const commitRename = async (): Promise<void> => {
    const v = editRef.current?.value?.trim();
    if (!v || v === panel.displayLabel) {
      setEditing(false);
      return;
    }
    try {
      await renamePanel(panel.id, v);
      onUpdate({ ...panel, displayLabel: v });
    } catch (err) {
      console.warn('[bulletin] rename failed:', err);
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (
      !window.confirm(
        `Delete panel "${panel.displayLabel}"? Bound task is preserved.`,
      )
    ) {
      return;
    }
    try {
      await apiDeletePanel(panel.id);
      onDelete(panel.id);
    } catch (err) {
      console.warn('[bulletin] delete failed:', err);
    }
  };

  const handleUnbind = async (): Promise<void> => {
    try {
      await bindPanel(panel.id, null);
      onUpdate({
        ...panel,
        linkedYesterdayId: null,
        status: 'pending',
        latestR2Key: null,
        outputFormat: null,
        lastError: null,
      });
    } catch (err) {
      console.warn('[bulletin] unbind failed:', err);
    }
  };

  const toggleCollapsed = async (): Promise<void> => {
    const next = !collapsed;
    setCollapsed(next); // optimistic
    try {
      await apiSetCollapsed(panel.id, next);
      onUpdate({ ...panel, collapsed: next });
    } catch (err) {
      console.warn('[bulletin] setCollapsed failed:', err);
      setCollapsed(!next); // revert
    }
  };

  return (
    <div
      className={
        'bulletin-panel' + (collapsed ? ' bulletin-panel-collapsed' : '')
      }
      style={{
        position: 'absolute',
        left: panel.x,
        top: panel.y,
        width: panel.w,
        // Collapsed: shrink to header + footer height regardless of stored h.
        height: collapsed ? 'auto' : panel.h,
        zIndex: panel.zIndex,
      }}
      role="region"
      aria-label={`Panel ${panel.koji}`}
    >
      <div
        className="bulletin-panel-header"
        onMouseDown={onHeaderMouseDown}
      >
        <span className="bulletin-drag-grip" aria-hidden="true">
          ⋮⋮
        </span>
        <code
          className="bulletin-koji"
          onClick={() => {
            void navigator.clipboard
              .writeText(panel.koji)
              .catch(() => undefined);
          }}
          title="Click to copy koji"
        >
          {panel.koji}
        </code>
        {editing ? (
          <input
            ref={editRef}
            defaultValue={panel.displayLabel}
            onBlur={() => void commitRename()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void commitRename();
              if (e.key === 'Escape') setEditing(false);
            }}
            autoFocus
            className="bulletin-rename-input"
          />
        ) : (
          <span
            className="bulletin-label"
            onDoubleClick={() => setEditing(true)}
            title="Double-click to rename"
          >
            {panel.displayLabel === panel.koji ? '' : panel.displayLabel}
          </span>
        )}
        <span
          className={`bulletin-status bulletin-status-${panel.status}`}
          aria-label={`status: ${panel.status}`}
        >
          ● {panel.status}
        </span>
        {/* Stage 39: collapse toggle. Collapsed panels are excluded
            from the daily briefing yesterday-task. */}
        <button
          type="button"
          className="bulletin-panel-collapse-btn"
          onClick={(e) => {
            e.stopPropagation();
            void toggleCollapsed();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
          title={
            collapsed
              ? 'Expand panel'
              : 'Collapse (excludes panel from briefing)'
          }
        >
          {collapsed ? '⊞' : '⊟'}
        </button>
      </div>

      {!collapsed ? (
        <div className="bulletin-panel-body">
          <BulletinPanelBody panel={panel} />
        </div>
      ) : null}

      <div className="bulletin-panel-footer">
        {/* Stage 33: version dropdown. Lazy-loads on first open;
            cache invalidates on last_run_at bump. Selecting "Latest"
            (empty value) unpins. */}
        <select
          className="bulletin-version-select"
          value={panel.pinnedR2Key ?? ''}
          onMouseDown={() => void loadHistory()}
          onFocus={() => void loadHistory()}
          onChange={(e) => void handlePinChange(e)}
          disabled={!panel.linkedYesterdayId}
          title={
            panel.pinnedR2Key
              ? `Pinned: ${panel.pinnedR2Key}`
              : 'Showing latest output'
          }
          aria-label="Panel version"
        >
          <option value="">Latest</option>
          {versions === null ? (
            panel.pinnedR2Key ? (
              <option value={panel.pinnedR2Key}>
                {panel.pinnedR2Key.split('/').pop() ?? 'pinned'}
              </option>
            ) : (
              <option value="" disabled>
                Loading…
              </option>
            )
          ) : versions.length === 0 ? (
            <option disabled>No history yet</option>
          ) : (
            versions.map((v) => (
              <option key={v.key} value={v.key}>
                {formatVersionLabel(v)}
              </option>
            ))
          )}
        </select>
        {pinError ? (
          <span
            className="bulletin-error small"
            title={pinError}
            aria-label={`pin error: ${pinError}`}
          >
            ⚠
          </span>
        ) : null}
        <button
          type="button"
          data-bulletin-bind-toggle
          className={
            'bulletin-footer-btn ' +
            (panel.linkedYesterdayId ? '' : 'bulletin-footer-btn-primary')
          }
          onClick={() => setBindOpen((o) => !o)}
          aria-expanded={bindOpen}
        >
          {panel.linkedYesterdayId ? 'Re-bind…' : 'Bind task…'}
        </button>
        {panel.linkedYesterdayId ? (
          <button
            type="button"
            className="bulletin-footer-btn"
            onClick={() => void handleUnbind()}
          >
            Unbind
          </button>
        ) : null}
        {panel.latestR2Key ? (
          <button
            type="button"
            className="bulletin-footer-btn"
            onClick={() => setHistoryOpen(true)}
          >
            History
          </button>
        ) : null}
        <button
          type="button"
          className="bulletin-footer-btn bulletin-footer-btn-danger"
          onClick={() => void handleDelete()}
        >
          Delete
        </button>

        {bindOpen ? (
          <BindTaskPopover
            panel={panel}
            onClose={() => setBindOpen(false)}
            onBound={(taskId) => {
              onUpdate({
                ...panel,
                linkedYesterdayId: taskId,
                // Rebinding clears any prior cached output, so the
                // panel transitions to 'linked' regardless of its
                // prior status. The next yesterday tick that runs the
                // bound task will flip it to 'ready'.
                status: 'linked',
                latestR2Key: null,
                outputFormat: null,
                lastError: null,
              });
              setBindOpen(false);
              void onRefresh();
            }}
          />
        ) : null}
      </div>

      <div
        className="bulletin-resize-handle"
        onMouseDown={onResizeMouseDown}
        aria-label="Resize panel"
        title="Drag to resize"
      >
        ◢
      </div>

      {historyOpen ? (
        <HistoryDrawer panel={panel} onClose={() => setHistoryOpen(false)} />
      ) : null}
    </div>
  );
}
